import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

export function apiErr(detail, err) {
  if (detail == null) {
    if (err && err.response) return `Erreur ${err.response.status}${err.response.statusText ? " (" + err.response.statusText + ")" : ""}. Réessayez.`;
    if (err && err.message) return err.message;
    return "Une erreur est survenue. Réessayez.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

let CURRENT_CURRENCY = "XOF";
export function setCurrency(c) { if (c) CURRENT_CURRENCY = c; }
export function getCurrency() { return CURRENT_CURRENCY; }

// Fuseau horaire de l'entreprise (configurable) — appliqué à tous les affichages d'heure.
let CURRENT_TZ = "Indian/Comoro";
export function setTimezone(t) { if (t) CURRENT_TZ = t; }
export function getTimezone() { return CURRENT_TZ; }
// Date calendaire (YYYY-MM-DD) dans le fuseau de l'entreprise — jamais toISOString() (UTC) pour les bornes de journée.
export function localDateISO(d = new Date()) { return new Date(d).toLocaleDateString("en-CA", { timeZone: CURRENT_TZ }); }
export function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: CURRENT_TZ }); }
  catch { return ""; }
}
export function fmtDateOnly(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: CURRENT_TZ }); }
  catch { return String(iso).slice(0, 10); }
}
export function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: CURRENT_TZ }); }
  catch { return String(iso); }
}

export function money(v, currency) {
  const cur = currency || CURRENT_CURRENCY;
  const n = Number(v || 0);
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n) + " " + cur;
}

// Échappe le texte contrôlé par l'utilisateur avant injection dans un document HTML imprimé (anti-XSS stocké).
export function escapeHtml(v) {
  return String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

export async function blobErrDetail(e) {
  const d = e?.response?.data;
  if (d instanceof Blob) {
    try { return JSON.parse(await d.text())?.detail; } catch { return null; }
  }
  return d?.detail;
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const supportsDownload = "download" in HTMLAnchorElement.prototype;
  const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (!supportsDownload || isIOS) {
    // iOS Safari / contextes restreints : a.download est ignoré → ouvrir le fichier dans un nouvel onglet (visualisable et enregistrable).
    const w = window.open(url, "_blank");
    if (!w) { window.location.href = url; }
    toast.success("Fichier généré — utilisez « Partager / Enregistrer » pour le sauvegarder.");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  toast.success(`Téléchargement lancé : ${filename}`);
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 10000);
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: CURRENT_TZ }); }
  catch { return iso; }
}

export default api;

export async function shareSalesReportPdf({ activityId, days = 1, locationId = "", sellerId = "", orgName = "Biashara", subtitle = "", locationLabel = "Tous", sellerLabel = "Tous" }) {
  const w = window.open("", "_blank");
  if (w) w.document.write("<p style='font-family:Arial;padding:24px;color:#475569'>Génération du PDF en cours…</p>");
  try {
    const to = new Date();
    const from = new Date(Date.now() - days * 86400000);
    const sinceIso = from.toISOString();
    let q = activityId ? `activity_id=${activityId}&days=${days}` : `days=${days}`;
    if (locationId) q += `&location_id=${locationId}`;
    if (sellerId) q += `&seller_id=${sellerId}`;
    const { data: d } = await api.get(`/reports/summary?${q}`);
    let sq = activityId ? `activity_id=${activityId}` : "";
    if (locationId) sq += `${sq ? "&" : ""}location_id=${locationId}`;
    if (sellerId) sq += `${sq ? "&" : ""}seller_id=${sellerId}`;
    let sales = [];
    try { const r = await api.get(`/sales${sq ? "?" + sq : ""}`); sales = (r.data || []).filter((s) => (s.created_at || "") >= sinceIso && !s.voided); } catch (e) { /* module ventes non accessible */ }
    const payload = {
      org: orgName, subtitle, period: `du ${from.toLocaleDateString("fr-FR", { timeZone: getTimezone() })} au ${to.toLocaleDateString("fr-FR", { timeZone: getTimezone() })}`,
      location_label: locationLabel, seller_label: sellerLabel, edited_at: fmtDateTime(to.toISOString()),
      kpis: [["Chiffre d'affaires", money(d.total_revenue)], ["Nombre de ventes", String(d.sales_count)], ["Panier moyen", money(d.avg_basket)], ["Marge estimée", money(d.margin)], ["Encaissé", money(d.total_paid)], ["Crédit en cours", money(d.total_credit)]],
      top_products: (d.top_products || []).map((p, i) => [`${i + 1}. ${p.name}`, String(p.qty), money(p.revenue), money(p.margin)]),
      product_margins: (d.product_margins || []).map((p) => [p.name, `${p.base_qty}${p.base_unit ? " " + p.base_unit : ""}`, money(p.revenue), money(p.cogs), money(p.margin), `${p.margin_pct}%`]),
      sales: sales.map((s) => [fmtDateTime(s.created_at), s.number || "", s.customer_name || "Comptant", s.seller_name || "", money(s.total), money(s.paid || 0), money(s.balance || 0)]),
    };
    const { data } = await api.post("/reports/pdf", payload);
    const waText = `*${orgName}* — Rapport des ventes (${payload.period})${sellerLabel && sellerLabel !== "Tous" ? " · Caissier : " + sellerLabel : ""}\n${data.url}`;
    if (w) w.location.href = `https://wa.me/?text=${encodeURIComponent(waText)}`;
    return data.url;
  } catch (e) {
    if (w) w.document.body.innerHTML = "<p style='font-family:Arial;padding:24px;color:#B91C1C'>Génération du PDF impossible. Réessayez.</p>";
    throw e;
  }
}

export async function openQuotePdf(qid) {
  const w = window.open("", "_blank");
  if (w) w.document.write("<p style='font-family:Arial;padding:24px;color:#475569'>Génération du devis PDF…</p>");
  try {
    const { data } = await api.post(`/quotes/${qid}/pdf-link`);
    if (w) w.location.href = data.url; else window.open(data.url, "_blank", "noopener,noreferrer");
    return data.url;
  } catch (e) {
    if (w) w.document.body.innerHTML = "<p style='font-family:Arial;padding:24px;color:#B91C1C'>Génération du devis impossible. Réessayez.</p>";
    throw e;
  }
}

export async function openInvoicePdf(sid) {
  const w = window.open("", "_blank");
  if (w) w.document.write("<p style='font-family:Arial;padding:24px;color:#475569'>Génération de la facture PDF…</p>");
  try {
    const { data } = await api.post(`/sales/${sid}/invoice-link`);
    if (w) w.location.href = data.url; else window.open(data.url, "_blank", "noopener,noreferrer");
    return data.url;
  } catch (e) {
    if (w) w.document.body.innerHTML = "<p style='font-family:Arial;padding:24px;color:#B91C1C'>Génération de la facture impossible. Réessayez.</p>";
    throw e;
  }
}

export function waLink(phone, text) {  const digits = String(phone || "").replace(/[^0-9]/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

export function fillTemplate(tpl, vars) {
  return String(tpl || "").replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? ""));
}

export function invoiceText(sale, template) {
  const f = (v) => new Intl.NumberFormat("fr-FR").format(Number(v || 0));
  const cur = CURRENT_CURRENCY;
  const hasDisc = (sale.lines || []).some((l) => Number(l.discount) > 0);
  const lines = (sale.lines || []).map((l) => {
    const d = Number(l.discount || 0);
    const name = l.description || `${l.product_name}${l.packaging && l.packaging !== "base" ? ` (${l.packaging})` : ""}`;
    const pct = l.discount_percent != null ? Number(l.discount_percent) : (l.unit_price ? d / l.unit_price * 100 : 0);
    const base = `• ${name} x${l.quantity} — P.U. ${f(l.unit_price)}`;
    if (!(d > 0)) return `${base} = ${f(l.unit_price * l.quantity)} ${cur}`;
    return `${base}\n   Remise −${pct.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} % (−${f(d * l.quantity)}) · net ${f(l.unit_price - d)}${l.negotiated ? " · Prix négocié" : ""} = ${f((l.unit_price - d) * l.quantity)} ${cur}`;
  }).join("\n");
  const gross = sale.gross_subtotal != null ? sale.gross_subtotal : (sale.lines || []).reduce((a, l) => a + l.unit_price * l.quantity, 0);
  const dtot = sale.discount_total != null ? sale.discount_total : (sale.lines || []).reduce((a, l) => a + (l.discount || 0) * l.quantity, 0);
  const discBlock = hasDisc ? `Sous-total avant remise: ${f(gross)} ${cur}\nTotal des remises: −${f(dtot)} ${cur}\n` : "";
  const change = sale.change_due != null ? `\nMontant reçu: ${f(sale.cash_received)} ${cur}\nMonnaie rendue: ${f(sale.change_due)} ${cur}` : "";
  const vars = { number: sale.number, client: sale.customer_name || "Client", lines, total: f(sale.total), paid: f(sale.paid), balance: f(sale.balance), currency: cur, discounts: discBlock, change, activity: sale.activity_name || "" };
  const tpl = template || "*Facture {number}*\nClient: {client}\n{lines}\n\n{discounts}Total net à payer: {total} {currency}\nPayé: {paid} {currency}{change}\nSolde dû: {balance} {currency}\n\nMerci pour votre confiance.";
  let out = fillTemplate(tpl, vars);
  // Identité = activité de la vente ; ajoutée en tête si le modèle ne la place pas lui-même
  if (sale.activity_name && !(template || "").includes("{activity}")) out = `*${sale.activity_name}*\n${out}`;
  // Modèle personnalisé sans détail remises : on l'ajoute pour garantir la cohérence facture/ticket/WhatsApp
  if (template && hasDisc && !template.includes("{discounts}")) out = out.replace(/\n\s*Total/, `\n${discBlock}Total`);
  return out;
}

// Vrai si la date ISO tombe aujourd'hui (heure locale) — utilisé pour n'autoriser l'annulation que le jour même.
export function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso), n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

export function within24h(iso) {
  if (!iso) return false;
  return (Date.now() - new Date(iso).getTime()) < 86400000;
}

// Imprime un document HTML dans un iframe caché : la fenêtre de l'appli reste ouverte, aucun onglet à fermer, retour immédiat après impression.
export function printHtml(html) {
  const iframe = document.createElement("iframe");
  Object.assign(iframe.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);
  const win = iframe.contentWindow;
  const doc = win.document;
  doc.open(); doc.write(html); doc.close();
  const remove = () => { try { iframe.remove(); } catch (e) { /* déjà retiré */ } };
  win.onafterprint = remove;
  setTimeout(() => { try { win.focus(); win.print(); } catch (e) { remove(); } }, 400);
  setTimeout(remove, 60000);
}
